import { store } from "@/redux/store";
import { FeatureKey } from "@/types/generated/FeatureKey";
import type { LoginInfo } from "@/types/LoginInfo";
import { hasFeature } from "@/hooks/useFeatures";
import { isPartnerUser, isSystemUser } from "@/utils/orgTypeUtils";

/**
 * STIC-15987: the functionalities gatekept at partner level - maintenance,
 * utilization KPIs, flows, and since STIC-16143 geozones. Each has a NORMAL key
 * (the entitlement of the customer organisation that OWNS the entity) and a
 * partner-level key (`*_multi_org`, or `legacy_partner_level_geozones`: what a
 * partner needs to own such entities itself and to see the pages at its own
 * level). The client-side mirror of the backend's PartnerLevelFeature; keep
 * both in step.
 *
 * The doc's principle for CRUD, which both helpers implement:
 *  - a PARTNER may always read / update / delete the entities of its child
 *    organisations on its permissions alone - no feature key on any of those
 *    endpoints, so the UI must not hide them behind the partner's own features;
 *  - only CREATE is gated, and against the organisation that will OWN the new
 *    entity: a child org on its NORMAL key, the partner account on its
 *    partner-level key. That check runs server-side against the target org;
 *    the client cannot see a customer's subscription, so it offers the action
 *    on permission and lets the server answer.
 *
 * Permissions are checked separately by each call site, as they always were.
 */

/**
 * Mirror of the API gate `(PARTNER || <normalKey>)`: may this login use the
 * functionality at all, inside the organisation in scope? A partner always may -
 * it cannot hold the NORMAL key (visibleFor NORMAL) and works on permissions;
 * everyone else needs the key.
 *
 * @param loginInfo pass it where the Redux store is not the source, the same
 *                  way the two-argument {@link hasFeature} is used.
 */
export const hasFeatureOrIsPartner = (normalKey: FeatureKey, loginInfo?: LoginInfo | null): boolean => {
  const login = loginInfo !== undefined ? loginInfo : safeLoginInfo();
  return isPartnerUser(login) || hasFeature(normalKey, login);
};

/**
 * May this login see the functionality at its OWN level (own-level admin menu,
 * home menu, the page's own-level list)? Stated per organisation type on
 * purpose rather than left to emerge from `Feature.visibleFor`:
 *
 *   NORMAL   ->  normalKey   - it owns its entities and holds the key
 *   PARTNER  ->  partnerKey  - its own-level, partner-owned or aggregated view;
 *                              it cannot hold the NORMAL key
 *   SYSTEM   ->  never       - excluded explicitly, not by feature:
 *                              system_full_access grants nearly every key, so
 *                              keying SYSTEM off a feature would read true and
 *                              leave the outcome resting on some other guard
 */
export const canSeeAtOwnLevel = (
  normalKey: FeatureKey,
  partnerKey: FeatureKey,
  loginInfo?: LoginInfo | null
): boolean => {
  const login = loginInfo !== undefined ? loginInfo : safeLoginInfo();
  if (isSystemUser(login)) {
    return false;
  }
  return isPartnerUser(login) ? hasFeature(partnerKey, login) : hasFeature(normalKey, login);
};

/** The store read that `hasFeature` does, with the same swallow-and-deny fallback. */
const safeLoginInfo = (): LoginInfo | null => {
  try {
    return store.getState()?.user ?? null;
  } catch {
    return null;
  }
};
